/* eslint-disable react/prop-types */
import React from "react";
import PropTypes from "prop-types";

// @material-ui/core
import withStyles from "@material-ui/core/styles/withStyles";

import { CircularProgressbar } from "react-circular-progressbar";

import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Modal from "@material-ui/core/Modal";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RefreshIcon from "@material-ui/icons/Refresh";
import SettingsApplicationsIcon from "@material-ui/icons/SettingsApplications";
import TimelineIcon from "@material-ui/icons/Timeline";
import AlarmIcon from "@material-ui/icons/Alarm";
import InfoIcon from "@material-ui/icons/Info";
import ListIcon from "@material-ui/icons/List";
import CodeIcon from "@material-ui/icons/Code";

// core component
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardAvatar from "components/Card/CardAvatar.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import ReactEcharts from "echarts-for-react";
import Table from "components/Table/Table.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";

import NavPills from "components/NavPills/NavPills.js";
import Button from "components/CustomButtons/Button.jsx";

import LCD from "components/LCD/LCD.jsx";
import DeviceDetailsTable from "components/DeviceDetailsTable/DeviceDetailsTable.jsx";
import CustomSelect from "components/CustomSelect/CustomSelect.jsx";
import AdvancedCharts from "views/AdvancedCharts/AdvancedCharts.jsx";

import {
  getUserDevices,
  downloadDeviceData,
  getDeviceAlarms,
  createDeviceAlarm,
  deleteDeviceAlarm,
  getDeviceAlarmHistory
} from "../../services/dashboardDataProvider";

import "react-circular-progressbar/dist/styles.css";
import { prettyJson } from "../../utils/converters.jsx";
import { MEDIA_ROOT } from "config.js";

import bulbIcon from "assets/img/bulb.webp";
import cflIcon from "assets/img/CFL.png";
import acIcon from "assets/img/air-conditioner.webp";
import fanIcon from "assets/img/fan.jpg";
import fridgeIcon from "assets/img/fridge.webp";
import pumpIcon from "assets/img/pump.png";
import motorIcon from "assets/img/motor.jpeg";

import {
  DATE_DISPLAY_FORMAT_DETAILED,
  dayjs
} from "utils/time.jsx";

import {
  PERMISSIONS_ADMIN,
  CAN_SEE_DEVICE_LOGS,
  CAN_SEE_RAW_DATA_DETAILS,
  RAW_DATA_KEYS_TO_HIDE
} from "utils/userPermissions.jsx";
import DeviceCommands from "../DeviceCommands/DeviceCommands.jsx";
import DeviceLogs from "../DeviceLogs/DeviceLogs.jsx";
import { isDarkUiTheme } from "theme/deviceDetailsTheme.js";
import {
  getAppShellUi,
  buildSharedPageShellStyles
} from "theme/appShellTheme.js";

const loadIcons = {
  Bulb: bulbIcon,
  CFL: cflIcon,
  AC: acIcon,
  Fan: fanIcon,
  Refrigerator: fridgeIcon,
  "Water pump": pumpIcon,
  "Agriculture Machine": motorIcon
};

const styles = theme => {
  const isDark = isDarkUiTheme(theme);
  const ui = getAppShellUi(isDark);
  const shell = buildSharedPageShellStyles(isDark);

  return {
  ...shell,
  pageShell: shell.pageShell,
  img: {
    width: "100%",
    maxWidth: "220px",
    height: "auto",
    verticalAlign: "middle",
    border: "0",
    borderRadius: "18px",
    boxShadow: "0 18px 36px rgba(15, 23, 42, 0.18)"
  },
  loadOnImg: {
    width: "60px",
    height: "auto",
    backgroundColor: "lightgreen"
  },
  loadOffImg: {
    width: "60px",
    height: "auto"
  },
  cardTitleBlack: {
    color: ui.text,
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "700",
    fontFamily: "'Josefin Sans', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "6px",
    letterSpacing: "0.02em",
    fontSize: "2rem",
    textDecoration: "none"
  },
  cardIconTitle: {
    color: ui.textMuted,
    margin: "10px 0 0",
    fontWeight: "300"
  },
  statusSummaryContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between"
  },
  statusSummaryIndicatorsContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap"
  },
  statusSummaryItem: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    margin: "10px"
  },
  StatusSummaryItemTitle: {
    minWidth: "130px !important"
  },
  StatusSummaryItemWidget: {
    width: "auto"
  },
  statusItemChip: {
    marginTop: "15px"
  },
  statusSummaryControls: {
    margin: "10px"
  },
  cardIcon: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  cardBody: {
    padding: "18px 22px 24px"
  },
  cardHover: shell.cardHover,
  cardHeaderHover: shell.cardHeaderHover,
  heroAvatarFrame: {
    overflow: "visible",
    boxShadow: ui.accentGlow
  },
  lcd: {
    borderRadius: "32px",
    marginTop: "24px"
  },
  deviceInfoHeaderContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "18px",
    flex: 1,
    padding: "20px 22px",
    background: ui.surfaceRaised,
    boxShadow: ui.shadowInset,
    borderRadius: "24px"
  },
  deviceInfo: {
    padding: "4px 2px",
    flex: "1 1 360px"
  },
  deviceTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap"
  },
  deviceMetaPill: {
    borderRadius: "999px",
    background: ui.pillBg,
    color: ui.textSecondary,
    padding: "7px 12px",
    fontSize: "0.8rem",
    fontWeight: "600",
    letterSpacing: "0.04em",
    textTransform: "uppercase"
  },
  timestampPanel: {
    marginTop: "12px",
    padding: "12px 14px",
    borderRadius: "16px",
    background: ui.surfaceInset,
    border: `1px solid ${ui.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap"
  },
  deviceWeatherInfo: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "18px",
    padding: "16px 18px",
    minWidth: "260px",
    borderRadius: "22px",
    background: ui.weatherPanel,
    border: `1px solid ${ui.border}`,
    boxShadow: isDark ? "none" : "0 12px 28px rgba(56, 189, 248, 0.12)"
  },
  cityName: {
    display: "flex",
    flexDirection: "column",
    alignItems: "baseline"
  },
  cityTemperature: {
    color: ui.text,
    fontSize: "3.2rem",
    fontWeight: "300",
    textAlign: "center"
  },
  cityWeatherImage: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column"
  },
  cityWeatherIcon: {
    height: "100px",
    width: "100px"
  },
  rawTelemetryDataContainer: {
    display: "flex",
    flexDirection: "column",
    overflowX: "scroll"
  },
  jsonContainer: {
    display: "flex",
    flexDirection: "column"
  },
  jsonKeyValuePair: {
    display: "flex",
    flexDirection: "row"
  },
  jsonKey: {
    fontSize: "larger",
    color: ui.jsonKey,
    display: "flex",
    flexDirection: "row"
  },
  jsonKeyValue: {
    display: "flex",
    flexDirection: "row",
    color: ui.jsonValue,
    paddingLeft: "5px"
  },
  updateInfo: {
    color: ui.text,
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap"
  },
  dataUpdateInfo: {
    color: ui.text,
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap"
  },
  updateInfoLabel: {
    color: ui.accent,
    fontWeight: "700"
  },
  dataUpdateInfoLabel: {
    color: isDark ? ui.accent : "#7c3aed",
    fontWeight: "700"
  },
  refreshButton: {
    marginLeft: "auto",
    boxShadow: isDark ? "none" : "0 10px 20px rgba(59, 130, 246, 0.2)"
  },
  chartSelectionContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap"
  },
  chartTypeLabel: {
    paddingRight: "10px"
  },
  chartTypeSelector: {
    width: "115px !important"
  },
  alarmActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "10px"
  },
  telemetrySubTabsBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "14px",
    padding: "10px 12px",
    borderRadius: "14px",
    background: ui.surfaceRaised,
    border: `1px solid ${ui.border}`,
    boxShadow: isDark ? "none" : "0 8px 18px rgba(15, 23, 42, 0.05)"
  },
  telemetrySubTabs: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "12px",
    border: `1px solid ${ui.borderStrong}`,
    background: ui.tabRail,
    padding: "2px 6px"
  },
  telemetryTabsRoot: {
    minHeight: 38
  },
  telemetryTabsIndicator: {
    height: "100%",
    borderRadius: "10px",
    background: ui.tabIndicator,
    boxShadow: isDark ? "none" : "0 8px 16px rgba(37, 99, 235, 0.28)",
    zIndex: 0
  },
  telemetrySubTabButton: {
    minWidth: "140px",
    minHeight: 36,
    fontWeight: "700",
    letterSpacing: "0.01em",
    textTransform: "none",
    zIndex: 1
  },
  telemetrySubTabButtonSelected: {
    color: `${ui.tabSelectedText} !important`
  },
  telemetrySubTabButtonInactive: {
    color: ui.tabIdleText,
    opacity: 0.92
  },
  telemetryDownloadButton: {
    fontWeight: "700",
    boxShadow: "0 8px 16px rgba(14, 165, 233, 0.26)"
  },
  statusPaginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "10px",
    marginBottom: "12px",
    padding: "10px 12px",
    borderRadius: "12px",
    background: ui.surfaceMuted,
    border: `1px solid ${ui.borderStrong}`
  },
  statusPaginationInfo: {
    color: ui.textSecondary,
    fontWeight: "600",
    background: ui.accentSoft,
    padding: "6px 10px",
    borderRadius: "999px"
  },
  statusPaginationButtons: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  alarmModalBox: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    opacity: "100%",
    zIndex: 2000,
    backgroundColor: ui.surfaceSolid,
    borderRadius: "10px",
    border: `1px solid ${ui.border}`,
    "-webkit-box-shadow": "0 3px 7px rgba(0, 0, 0, 0.3)",
    "-moz-box-shadow": "0 3px 7px rgba(0, 0, 0, 0.3)",
    boxShadow: "0 3px 7px rgba(0, 0, 0, 0.3)",
    "-webkit-background-clip": "padding-box",
    "-moz-background-clip": "padding-box",
    backgroundClip: "padding-box"
  },
  rawDataModalBox: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "min(960px, 92vw)",
    maxHeight: "88vh",
    opacity: "100%",
    zIndex: 2000,
    backgroundColor: ui.surfaceSolid,
    borderRadius: "14px",
    border: `1px solid ${ui.border}`,
    boxShadow: isDark ? "0 24px 48px rgba(0, 0, 0, 0.55)" : "0 24px 48px rgba(15, 23, 42, 0.18)",
    backgroundClip: "padding-box",
    display: "flex",
    flexDirection: "column"
  },
  rawDataModalHeader: {
    padding: "14px 18px 8px",
    borderBottom: `1px solid ${ui.border}`
  },
  rawDataModalBody: {
    overflowY: "auto",
    padding: "10px 14px",
    flex: 1
  },
  chartPointSummary: {
    marginBottom: "12px",
    padding: "10px 12px",
    borderRadius: "10px",
    border: `1px solid ${ui.borderStrong}`,
    background: ui.surfaceInset
  },
  alarmSelectionContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px"
  },
  alarmsContainer: {
    ...shell.sectionPanel,
    padding: "14px 16px"
  },
  alarmTypeSelector: {
    minWidth: "150px"
  },
  heading: {
    fontWeight: "700",
    color: ui.text
  },
  marginRight: {
    marginRight: "14px"
  },
  footerLinks: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px"
  },
  footerLinkGroup: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px"
  },
  footerLink: {
    color: ui.accent,
    fontWeight: "700",
    textDecoration: "none",
    padding: "8px 12px",
    borderRadius: "999px",
    background: ui.accentSoft
  }
};
};

const ALARM_OPERATORS = [
  { text: "Equals", value: "eq" },
  { text: "Not equals", value: "neq" },
  { text: "Greater than", value: "gt" },
  { text: "Greater than or equals", value: "gte" },
  { text: "Less than", value: "lt" },
  { text: "Less than or equals", value: "lte" },
  { text: "Contains", value: "contains" }
];

const ALARM_CHANNEL_IN_APP = "in_app";
const ALARM_CHANNEL_TELEGRAM = "telegram";
const ALARM_CHANNEL_EMAIL = "email";

class DeviceDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      deviceId: null,
      showTodaysStatus: true,
      latestTelemetryView: "raw",
      statusDataPage: 0,
      visibleChartType: {},
      dataDownloadModalOpen: false,
      downloadData: {
        dataType: "raw_data",
        startDate: "",
        endDate: ""
      },
      alarmModalOpen: false,
      alarmsLoading: false,
      alarmsError: null,
      alarmRules: [],
      alarmHistory: [],
      alarmData: {
        name: "",
        status_key: "",
        operator: ALARM_OPERATORS[0].value,
        target_value: "",
        notifyInApp: true,
        notifyTelegram: false,
        notifyEmail: false,
        emails: "",
        telegram_chat_ids: ""
      },
      mainChartDate: "",
      mainChartStatusData: null,
      chartRawDataModalOpen: false,
      chartRawDataTimestamp: null,
      chartRawDataTooltipDetails: null,
      chartRawData: [],
      chartRawDataLoading: false,
      chartRawDataError: null
    };
  }

  fetchMainChartStatusData = selectedDate => {
    const deviceId = this.props.match.params && this.props.match.params.deviceId;

    if (!deviceId || !selectedDate) {
      this.setState({ mainChartStatusData: null });
      return;
    }

    const payload = {
      exportType: "json",
      dataType: "status",
      startDate: selectedDate
    };

    downloadDeviceData(this.props.axios, deviceId, payload).then(data => {
      this.setState({ mainChartStatusData: data || [] });
    });
  };

  getMainChartDateFromUrl = () => {
    const location = this.props.location || {};
    const search = location.search || "";
    if (!search) {
      return "";
    }

    const query = new URLSearchParams(search);
    const dateValue = (query.get("date") || "").trim();
    if (!dateValue) {
      return "";
    }

    const isValidDate = dayjs(dateValue, "YYYY-MM-DD", true).isValid();
    return isValidDate ? dateValue : "";
  };

  setMainChartDateInUrl = dateValue => {
    const location = this.props.location || {};
    const history = this.props.history;
    if (!history || !location.pathname) {
      return;
    }

    const query = new URLSearchParams(location.search || "");
    if (dateValue) {
      query.set("date", dateValue);
    } else {
      query.delete("date");
    }

    const nextSearch = query.toString();
    const currentSearch = (location.search || "").replace(/^\?/, "");
    if (nextSearch === currentSearch) {
      return;
    }

    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : ""
    });
  };

  onSelectionChange = (dictName, propName) => value => {
    if (dictName == null || dictName === "") {
      this.setState({ [propName]: value });
    } else {
      let updatedData = this.state[dictName] || {};
      updatedData[propName] = value;
      this.setState({ [dictName]: updatedData });
    }
  };

  onChange = (dictName, propName) => e => {
    const value = e.target.value;
    if (dictName == null || dictName === "") {
      this.setState({ [propName]: value });
    } else {
      let updatedData = this.state[dictName] || {};
      updatedData[propName] = value;
      this.setState({ [dictName]: updatedData });
    }
  };

  onChartDateChange = (idx, propName) => e => {
    const value = e.target.value;

    if (idx == null) {
      this.setState({ mainChartDate: value });
      this.setMainChartDateInUrl(value);
      this.fetchMainChartStatusData(value);
      return;
    }

    const visibleCharts = this.state.visibleCharts || [];
    if (visibleCharts[idx] == null) {
      visibleCharts[idx] = {};
    }
    visibleCharts[idx][propName] = value;
    this.setState({ visibleCharts: visibleCharts });

    let payload = visibleCharts[idx] || {};
    payload["exportType"] = "json";
    payload["dataType"] = "status";
    payload["statusData"] = null;
    payload["startDate"] = payload["date"];
    let deviceId = this.props.match.params && this.props.match.params.deviceId;
    if (deviceId) {
      downloadDeviceData(this.props.axios, deviceId, payload).then(data => {
        visibleCharts[idx]["statusData"] = data;
        this.setState({ visibleCharts: visibleCharts });
      });
    }
  };

  onChartTypeChange = (idx, propName) => value => {
    if (idx == null) {
      this.setState({ [propName]: value });
    } else {
      const visibleCharts = this.state.visibleCharts || [];
      if (visibleCharts[idx] == null) {
        visibleCharts[idx] = {};
      }
      visibleCharts[idx][propName] = value;
      this.setState({ visibleCharts: visibleCharts });
    }
  };

  downloadDeviceData() {
    let payload = this.state.downloadData || {};
    payload["exportType"] = "csv";
    let deviceId = this.props.match.params && this.props.match.params.deviceId;
    if (deviceId) {
      downloadDeviceData(this.props.axios, deviceId, payload).then(data => {
        const blob = new Blob([data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        a.setAttribute("href", url);
        a.setAttribute("download", "device-raw-data.csv");

        a.click();
        this.setState({ dataDownloadModalOpen: false });
      });
    }
  }

  componentDidMount() {
    // eslint-disable-next-line react/prop-types
    let deviceId = this.props.match.params && this.props.match.params.deviceId;
    if (deviceId) {
      getUserDevices(this.props.axios, deviceId).then(data => {
        this.setState({ deviceId: deviceId, deviceData: data });
      });
      this.refreshAlarmData(deviceId);

      const dateFromUrl = this.getMainChartDateFromUrl();
      if (dateFromUrl) {
        this.setState({ mainChartDate: dateFromUrl });
        this.fetchMainChartStatusData(dateFromUrl);
      }
    }
  }

  getStatusKeysFromDeviceData = deviceData => {
    const statusKeys = new Set();
    const availableStatusTypes = deviceData.available_status_types || [];

    const addSchemaFieldTargets = schema => {
      if (schema == null) {
        return;
      }

      if (Array.isArray(schema)) {
        schema.forEach(schemaItem => addSchemaFieldTargets(schemaItem));
        return;
      }

      if (typeof schema !== "object") {
        return;
      }

      if (typeof schema.target === "string" && schema.target.trim() !== "") {
        statusKeys.add(schema.target.trim());
      }

      const schemaFields = schema.fields;
      if (Array.isArray(schemaFields)) {
        schemaFields.forEach(field => {
          if (
            field != null &&
            typeof field === "object" &&
            typeof field.target === "string" &&
            field.target.trim() !== ""
          ) {
            statusKeys.add(field.target.trim());
          }
        });
      }
    };

    availableStatusTypes.forEach(statusType => {
      addSchemaFieldTargets(statusType.translation_schema);
    });

    if (statusKeys.size === 0) {
      const latestStatus = this.getLatestMergedStatus(deviceData) || {};
      Object.keys(latestStatus).forEach(key => {
        const value = latestStatus[key];
        if (value != null && typeof value !== "object") {
          statusKeys.add(key);
        }
      });
    }

    return Array.from(statusKeys).sort();
  };

  resetAlarmForm = statusKeys => {
    this.setState({
      alarmData: {
        name: "",
        status_key: statusKeys[0] || "",
        operator: ALARM_OPERATORS[0].value,
        target_value: "",
        notifyInApp: true,
        notifyTelegram: false,
        notifyEmail: false,
        emails: "",
        telegram_chat_ids: ""
      }
    });
  };

  refreshAlarmData = deviceId => {
    if (!deviceId) {
      return;
    }

    this.setState({ alarmsLoading: true, alarmsError: null });

    Promise.all([
      getDeviceAlarms(this.props.axios, deviceId),
      getDeviceAlarmHistory(this.props.axios, deviceId, 100)
    ])
      .then(([alarmRules, alarmHistory]) => {
        this.setState({
          alarmRules: Array.isArray(alarmRules) ? alarmRules : [],
          alarmHistory: Array.isArray(alarmHistory) ? alarmHistory : [],
          alarmsLoading: false,
          alarmsError: null
        });
      })
      .catch(() => {
        this.setState({
          alarmsLoading: false,
          alarmsError: "Unable to load alarms right now."
        });
      });
  };

  openAlarmModal = deviceData => {
    const statusKeys = this.getStatusKeysFromDeviceData(deviceData);
    this.resetAlarmForm(statusKeys);
    this.setState({ alarmModalOpen: true });
  };

  getAlarmChannels = alarmData => {
    const channels = [];
    if (alarmData.notifyInApp) {
      channels.push(ALARM_CHANNEL_IN_APP);
    }
    if (alarmData.notifyTelegram) {
      channels.push(ALARM_CHANNEL_TELEGRAM);
    }
    if (alarmData.notifyEmail) {
      channels.push(ALARM_CHANNEL_EMAIL);
    }
    return channels;
  };

  saveAlarmRule = () => {
    const deviceId = this.props.match.params && this.props.match.params.deviceId;
    if (!deviceId) {
      return;
    }

    const alarmData = this.state.alarmData || {};
    const channels = this.getAlarmChannels(alarmData);
    if (!alarmData.status_key || !alarmData.target_value) {
      this.setState({ alarmsError: "Status field and target value are required." });
      return;
    }

    const payload = {
      name: alarmData.name,
      status_key: alarmData.status_key,
      operator: alarmData.operator,
      target_value: alarmData.target_value,
      channels: channels,
      emails: alarmData.notifyEmail ? alarmData.emails : "",
      telegram_chat_ids: alarmData.notifyTelegram ? alarmData.telegram_chat_ids : "",
      active: true
    };

    this.setState({ alarmsLoading: true, alarmsError: null });
    createDeviceAlarm(this.props.axios, deviceId, payload)
      .then(() => {
        this.setState({ alarmModalOpen: false });
        this.refreshAlarmData(deviceId);

        // Refresh device payload to keep legacy alarm count in sync.
        getUserDevices(this.props.axios, deviceId).then(data => {
          this.setState({ deviceData: data });
        });
      })
      .catch(() => {
        this.setState({
          alarmsLoading: false,
          alarmsError: "Unable to save alarm rule."
        });
      });
  };

  removeAlarmRule = alarmId => {
    const deviceId = this.props.match.params && this.props.match.params.deviceId;
    if (!deviceId || !alarmId) {
      return;
    }

    this.setState({ alarmsLoading: true, alarmsError: null });
    deleteDeviceAlarm(this.props.axios, deviceId, alarmId)
      .then(() => {
        this.refreshAlarmData(deviceId);
      })
      .catch(() => {
        this.setState({
          alarmsLoading: false,
          alarmsError: "Unable to delete alarm rule."
        });
      });
  };

  renderStatusSummary(deviceData) {
    const { classes } = this.props;
    if (deviceData.properties == null) {
      return null;
    }

    const latestStatus = this.getLatestMergedStatus(deviceData) || {};
    const properties = deviceData.properties;
    const parseNumeric = value => {
      const parsed = parseFloat(value);
      return Number.isNaN(parsed) ? null : parsed;
    };
    const pickFirstNumeric = (...values) => {
      for (let idx = 0; idx < values.length; idx += 1) {
        const parsed = parseNumeric(values[idx]);
        if (parsed != null) {
          return parsed;
        }
      }
      return 0;
    };

    const currency = properties.currency || "$";
    const performanceTrackerTitle =
      properties.performance_tracker_title || "Savings this month:";
    const monthlyBillLimit = parseNumeric(properties.monthly_bill_limit) || 1;

    // Use latest status values for non-limit keys, and keep limits from properties.
    let monthlyBillAmount = pickFirstNumeric(
      latestStatus.monthly_bill_amount,
      properties.monthly_bill_amount
    );

    let energyConsumptionLimit =
      parseNumeric(properties.energy_consumption_limit_this_month) || 1;
    let energyConsumptionAmount = pickFirstNumeric(
      latestStatus.energy_consumed_this_month,
      properties.energy_consumed_this_month
    );

    let energyGenerationLimit =
      parseNumeric(properties.energy_generation_limit_this_month) || 1;
    let energyGenerationAmount = pickFirstNumeric(
      latestStatus.energy_generated_this_month,
      properties.energy_generated_this_month
    );

    let energyExportedAmount = pickFirstNumeric(
      latestStatus.energy_exported_this_month,
      properties.energy_exported_this_month
    );

    let energyImportedAmount = pickFirstNumeric(
      latestStatus.energy_imported_this_month,
      properties.energy_imported_this_month
    );

    if (this.state.showTodaysStatus) {
      energyConsumptionLimit =
        parseNumeric(properties.energy_consumption_limit_this_day) || 1;
      energyConsumptionAmount = pickFirstNumeric(
        latestStatus.energy_consumed_this_day,
        properties.energy_consumed_this_day
      );

      energyGenerationLimit =
        parseNumeric(properties.energy_generation_limit_this_day) || 1;
      energyGenerationAmount = pickFirstNumeric(
        latestStatus.energy_generated_this_day,
        properties.energy_generated_this_day
      );

      energyExportedAmount = pickFirstNumeric(
        latestStatus.energy_exported_this_day,
        properties.energy_exported_this_day
      );

      energyImportedAmount = pickFirstNumeric(
        latestStatus.energy_imported_this_day,
        properties.energy_imported_this_day
      );
    }

    const percentage = ((monthlyBillAmount * 100) / monthlyBillLimit).toFixed(1);
    const percentageEnergyConsumption = (
      (energyConsumptionAmount * 100) /
      energyConsumptionLimit
    ).toFixed(1);
    const percentageEnergyGeneration = (
      (energyGenerationAmount * 100) /
      energyGenerationLimit
    ).toFixed(1);

    return (
      <div className={classes.statusSummaryContainer}>
        <div className={classes.statusSummaryIndicatorsContainer}>
          <div className={classes.statusSummaryItem}>
            <h4 className={classes.StatusSummaryItemTitle}>
              {performanceTrackerTitle}
            </h4>
            <CircularProgressbar
              className={classes.StatusSummaryItemWidget}
              value={percentage}
              text={`${monthlyBillAmount.toFixed(1)} ${currency}`}
            />
            <Chip
              label={`Rate ${deviceData.properties.pay_per_unit} ${currency}`}
              color="primary"
              variant="outlined"
              className={classes.statusItemChip}
            />
          </div>
          <div className={classes.statusSummaryItem}>
            <h4 className={classes.StatusSummaryItemTitle}>
              Energy generated(KWh)
            </h4>
            <CircularProgressbar
              className={classes.StatusSummaryItemWidget}
              value={percentageEnergyGeneration}
              text={`${energyGenerationAmount.toFixed(1)}`}
            />
            <Chip
              label={`Exported ${energyExportedAmount.toFixed(1)} KWh`}
              color="primary"
              variant="outlined"
              className={classes.statusItemChip}
            />
          </div>
          <div className={classes.statusSummaryItem}>
            <h4 className={classes.StatusSummaryItemTitle}>
              Energy consumed(KWh)
            </h4>
            <CircularProgressbar
              className={classes.StatusSummaryItemWidget}
              value={percentageEnergyConsumption}
              text={`${energyConsumptionAmount.toFixed(1)}`}
            />
            <Chip
              label={`Imported ${energyImportedAmount.toFixed(1)} KWh`}
              color="primary"
              variant="outlined"
              className={classes.statusItemChip}
            />
          </div>
        </div>
        <div className={classes.statusSummaryControls}>
          <FormControlLabel
            control={
              <Switch
                checked={this.state.showTodaysStatus}
                onChange={() => {
                  this.setState({
                    showTodaysStatus: !this.state.showTodaysStatus
                  });
                }}
              />
            }
            label="Show Today"
          />
        </div>
      </div>
    );
  }

  renderDeviceTips(deviceData) {
    const deviceTips = deviceData.tips || [];
    return (
      deviceTips.length > 0 && (
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <h3>
              <strong>Tips for today</strong>
            </h3>
            <Table
              tableHeaderColor="primary"
              tableData={deviceTips.map(tip => [tip])}
              coloredColls={[3]}
              colorsColls={["primary"]}
            />
          </GridItem>
        </GridContainer>
      )
    );
  }

  transformValue = (dataType, status, valueField) => {
    let value = null;
    value = status[valueField];
    if (value == null) return value;
    if (typeof value === "number") return value.toFixed(2);
    if (dataType === "battery_charging_status") {
      if (typeof value === "string") {
        if (value.includes("Charging")) {
          value = value.replace("Charging", "").replace(" W", "");
          const numValue = parseFloat(value);
          return isNaN(numValue) ? value : numValue.toFixed(2);
        }
        if (value.includes("Discharging")) {
          value = value.replace("Discharging", "").replace(" W", "");
          const numValue = parseFloat(value);
          return isNaN(numValue) ? value : (-1 * numValue).toFixed(2);
        }
      }
    }

    if (dataType === "solar_status" || dataType === "load_status") {
      return typeof value === "string" ? value.replace(" W", "") : value;
    }

    if (
      dataType === "energy_generated_this_day" ||
      dataType === "energy_consumed_this_day"
    ) {
      const numValue = parseFloat(value);
      return isNaN(numValue) ? value : numValue.toFixed(2);
    }

    if (dataType === "net_meter_status") {
      return value;
    }

    if (dataType === "system_temperature") {
      if (typeof value === "string") {
        if (value.includes(" C, ")) {
          value = value.split(" C, ")[0];
        }
        value = value.replace(" C", "").trim();
        const numValue = parseFloat(value);
        return isNaN(numValue) ? value : numValue.toFixed(2);
      }
      return value;
    }

    if (dataType === "system_humidity") {
      if (typeof value === "number") {
        return value.toFixed(2);
      }

      if (
        typeof value === "string" &&
        value.includes(" C, ") &&
        value.includes(" H")
      ) {
        value = value.split(" C, ")[1].replace(" H", "");
        const numValue = parseFloat(value);
        return isNaN(numValue) ? value : numValue.toFixed(2);
      }

      if (typeof value === "string") {
        const trimmedValue = value.replace(" H", "").trim();
        const numValue = parseFloat(trimmedValue);

        return isNaN(numValue) ? null : numValue.toFixed(2);
      }

      return null;
    }
    return value;
  };

  handleChartRawDataRequest = (timestamp, tooltipDetails = null) => {
    if (!timestamp) {
      return;
    }

    const deviceId = this.props.match.params && this.props.match.params.deviceId;
    if (!deviceId) {
      return;
    }

    const centerTime = dayjs(timestamp);

    if (!centerTime.isValid()) {
      this.setState({
        chartRawDataModalOpen: true,
        chartRawDataTimestamp: timestamp,
        chartRawDataTooltipDetails: tooltipDetails,
        chartRawData: [],
        chartRawDataLoading: false,
        chartRawDataError: "Invalid chart timestamp."
      });
      return;
    }

    const payload = {
      exportType: "json",
      dataType: "raw_data",
      startTime: centerTime
        .subtract(5, "minute")
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss[Z]"),
      endTime: centerTime
        .add(5, "minute")
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss[Z]")
    };

    this.setState({
      chartRawDataModalOpen: true,
      chartRawDataTimestamp: timestamp,
      chartRawDataTooltipDetails: tooltipDetails,
      chartRawData: [],
      chartRawDataLoading: true,
      chartRawDataError: null
    });

    downloadDeviceData(this.props.axios, deviceId, payload)
      .then(data => {
        this.setState({
          chartRawData: Array.isArray(data) ? data : [],
          chartRawDataLoading: false,
          chartRawDataError: null
        });
      })
      .catch(() => {
        this.setState({
          chartRawData: [],
          chartRawDataLoading: false,
          chartRawDataError: "Unable to load raw data for the selected time window."
        });
      });
  };

  renderChartRawDataModal() {
    const { classes, user } = this.props;
    const userPermissions = user.user_permissions || [];
    const userCanSeeRawDataDetails = userPermissions.includes(
      CAN_SEE_RAW_DATA_DETAILS
    );
    const keysToHide = userCanSeeRawDataDetails ? [] : RAW_DATA_KEYS_TO_HIDE;
    const {
      chartRawDataModalOpen,
      chartRawDataTimestamp,
      chartRawDataTooltipDetails,
      chartRawData,
      chartRawDataLoading,
      chartRawDataError
    } = this.state;

    return (
      <Modal
        hideBackdrop
        open={chartRawDataModalOpen}
        onClose={() => this.setState({ chartRawDataModalOpen: false })}
        aria-labelledby="Raw data around point"
        aria-describedby="Raw telemetry records around selected chart point"
      >
        <Box className={classes.rawDataModalBox}>
          <div className={classes.rawDataModalHeader}>
            <Typography variant="h6">Raw data around selected point</Typography>
            <Typography variant="body2" color="textSecondary">
              {chartRawDataTimestamp
                ? `Center time: ${dayjs(chartRawDataTimestamp).format(
                    DATE_DISPLAY_FORMAT_DETAILED
                  )}`
                : ""}
            </Typography>
          </div>
          <div className={classes.rawDataModalBody}>
            {chartRawDataTooltipDetails && (
              <div className={classes.chartPointSummary}>
                <Typography variant="subtitle2">
                  Clicked point details
                </Typography>
                <Typography variant="body2">
                  Time: {chartRawDataTooltipDetails.timeLabel}
                </Typography>
                {(chartRawDataTooltipDetails.series || []).map((seriesItem, idx) => (
                  <Typography variant="body2" key={`series-item-${idx}`}>
                    {seriesItem.label}: {seriesItem.value}
                  </Typography>
                ))}
                {chartRawDataTooltipDetails.systemStatus != null &&
                  chartRawDataTooltipDetails.systemStatus !== "" && (
                    <Typography variant="body2">
                      System Status: {chartRawDataTooltipDetails.systemStatus}
                    </Typography>
                  )}
                {chartRawDataTooltipDetails.netMeterStatus != null &&
                  chartRawDataTooltipDetails.netMeterStatus !== "" && (
                    <Typography variant="body2">
                      Net Meter Status: {chartRawDataTooltipDetails.netMeterStatus}
                    </Typography>
                  )}
                {chartRawDataTooltipDetails.powerFactor != null &&
                  chartRawDataTooltipDetails.powerFactor !== "" && (
                    <Typography variant="body2">
                      Power Factor: {chartRawDataTooltipDetails.powerFactor}
                    </Typography>
                  )}
              </div>
            )}
            {chartRawDataLoading && <Typography>Loading raw data...</Typography>}
            {!chartRawDataLoading && chartRawDataError && (
              <Typography color="error">{chartRawDataError}</Typography>
            )}
            {!chartRawDataLoading && !chartRawDataError && chartRawData.length === 0 && (
              <Typography>No raw data found in this time window.</Typography>
            )}
            {!chartRawDataLoading && !chartRawDataError && chartRawData.length > 0 &&
              chartRawData.map((rawPoint, idx) => (
                <ExpansionPanel key={`chart-raw-data-${idx}`} defaultExpanded={idx === 0}>
                  <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography className={classes.heading}>
                      Updated: {dayjs.utc(rawPoint.data_arrival_time).local().fromNow()}
                    </Typography>
                  </ExpansionPanelSummary>
                  <ExpansionPanelDetails>
                    {prettyJson(rawPoint, classes, keysToHide)}
                  </ExpansionPanelDetails>
                </ExpansionPanel>
              ))}
          </div>
          <div className={classes.alarmSelectionContainer}>
            <Button onClick={() => this.setState({ chartRawDataModalOpen: false })}>
              Close
            </Button>
          </div>
        </Box>
      </Modal>
    );
  }

  getMergedStatus = (statusData, statusNames = []) => {
    const rawStatus = statusData && statusData.status;

    if (rawStatus == null || typeof rawStatus !== "object") {
      return {};
    }

    if (statusNames.length === 0) {
      return rawStatus;
    }

    const mergedStatus = statusNames.reduce((combined, statusName) => {
      const nextStatus = rawStatus[statusName];

      if (nextStatus != null && typeof nextStatus === "object") {
        return {
          ...combined,
          ...nextStatus
        };
      }

      return combined;
    }, {});

    return Object.keys(mergedStatus).length > 0 ? mergedStatus : rawStatus;
  };

  isNumericStatusValue = value => {
    if (typeof value === "number") {
      return !Number.isNaN(value) && Number.isFinite(value);
    }

    if (typeof value !== "string") {
      return false;
    }

    const trimmedValue = value.trim();

    return trimmedValue !== "" && !Number.isNaN(Number(trimmedValue));
  };

  formatChartKeyLabel = key => {
    return key
      .split("_")
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };

  getStatusChartSeries = (allStatusData = [], statusNames = []) => {
    const statusKeys = new Set();

    allStatusData.forEach(statusData => {
      const mergedStatus = this.getMergedStatus(statusData, statusNames);

      Object.keys(mergedStatus).forEach(statusKey => {
        const value = mergedStatus[statusKey];
        if (
          statusKey.endsWith("_status") &&
          this.isNumericStatusValue(value)
        ) {
          statusKeys.add(statusKey);
        }
      });
    });

    return Array.from(statusKeys)
      .sort()
      .map(statusKey => ({
        dataKey: statusKey,
        dataType: statusKey,
        valueField: statusKey,
        label: this.formatChartKeyLabel(statusKey)
      }));
  };

  wordsMatch = (text, query) => {
    const queryWords = query.toLowerCase().split(/\s+|_+/);
    return queryWords.some(word => text.toLowerCase().includes(word));
  };

  getChartTypeOptions = (allStatusData = [], statusNames = []) => {
    const availableStatusKeys = new Set();
    const numericStatusKeys = new Set();
    allStatusData.forEach(statusData => {
      const mergedStatus = this.getMergedStatus(statusData, statusNames);

      Object.keys(mergedStatus).forEach(statusKey => {
        const value = mergedStatus[statusKey];

        if (value == null || typeof value === "object") {
          return;
        }

        availableStatusKeys.add(statusKey);

        if (this.isNumericStatusValue(value)) {
          numericStatusKeys.add(statusKey);
        }
      });
    });

    let groupedOptions =[];

    Array.from(numericStatusKeys)
      .sort()
      .forEach(statusKey => {
        let statusKeyName = this.formatChartKeyLabel(statusKey);
        let matchingOption = groupedOptions.find(option => this.wordsMatch(option.text, statusKeyName));
        if (matchingOption) {
          matchingOption.series.push({
            dataKey: statusKey,
            dataType: statusKey,
            valueField: statusKey,
            label: statusKeyName
          });
        } else {
            groupedOptions.push({
            text: statusKeyName,
            value: statusKey,
            series: [
              {
                dataKey: statusKey,
                dataType: statusKey,
                valueField: statusKey,
                label: statusKeyName
              }
            ],
            isGeneric: true
            });
          }
        });
    return groupedOptions;
  };

  getLatestMergedStatus = deviceData => {
    const allStatusData = deviceData.status_data_today || [];

    if (allStatusData.length === 0) {
      return {};
    }

    const availableStatusTypes = deviceData.available_status_types || [];
    const statusNames = availableStatusTypes.map(st => st.name);
    const latestStatusData = allStatusData[allStatusData.length - 1];

    return this.getMergedStatus(latestStatusData, statusNames);
  };

  renderConsumptionChart(
    deviceData,
    showDateSelection = false,
    chartIdx = null
  ) {
    const tz =
      (deviceData.other_data && deviceData.other_data.timezone) ||
      dayjs.tz.guess();
    const { classes } = this.props;
    const availableStatusTypes = deviceData.available_status_types;
    let statusNames = [];
    if (availableStatusTypes) {
      statusNames = availableStatusTypes.map(st => st.name);
    }

    const visibleCharts = this.state.visibleCharts || [];
    const chartInputData = visibleCharts[chartIdx];
    let visibleChartType = this.state.visibleChartType;
    let selectedDay = "Today's";
    let allStatusData = deviceData.status_data_today;
    let selectedChartDate = this.state.mainChartDate || "";

    if (chartIdx == null && this.state.mainChartDate) {
      selectedDay = `${this.state.mainChartDate}'s`;
      allStatusData = this.state.mainChartStatusData || [];
    }

    if (chartInputData != null) {
      visibleChartType = chartInputData.visibleChartType;
      selectedDay = `${chartInputData.date}'s`;
      allStatusData = chartInputData.statusData || [];
      selectedChartDate = chartInputData.date || "";
    }

    const chartTypeOptions = this.getChartTypeOptions(allStatusData, statusNames);
    console.log("chartTypeOptions", chartTypeOptions);
    const selectedChartOption =
      chartTypeOptions.find(option => option.value === visibleChartType) ||
      chartTypeOptions[0] || {};
    const chartSeries =
      selectedChartOption.value === "status"
        ? this.getStatusChartSeries(allStatusData, statusNames)
        : selectedChartOption.series || [];
    const selectedChartLabel = selectedChartOption.text;
    visibleChartType = selectedChartOption.value;

    let dataToday =
      allStatusData &&
      allStatusData.map(statusData => {
        const status = this.getMergedStatus(statusData, statusNames);

        const chartValues = chartSeries.reduce((seriesData, seriesConfig) => {
          let seriesValue = this.transformValue(
            seriesConfig.dataType,
            status,
            seriesConfig.valueField
          );

          if (
            seriesValue == null &&
            seriesConfig.fallbackValueField != null &&
            seriesConfig.fallbackValueField !== seriesConfig.valueField
          ) {
            seriesValue = this.transformValue(
              seriesConfig.dataType,
              status,
              seriesConfig.fallbackValueField
            );
          }

          seriesData[seriesConfig.dataKey] = seriesValue;

          return seriesData;
        }, {});

        return {
          data_arrival_time: statusData.created_at,
          system_status: status.system_status,
          net_meter_status: status.net_meter_status,
          net_meter_power_factor: status.net_meter_power_factor,
          ...chartValues
        };
      }).sort((a, b) => new Date(a.data_arrival_time) - new Date(b.data_arrival_time));
      
    const visibleChartSeries = chartSeries.filter(seriesConfig => {
      if (seriesConfig.dataKey !== "system_humidity") {
        return true;
      }

      return dataToday.some(dataPoint => {
        const value = dataPoint[seriesConfig.dataKey];

        return value != null && value !== "";
      });
    });

    const weatherIconSize = 80;
    let lastRenderedWeatherPoint = null;

    const weatherData =
      allStatusData &&
      allStatusData.map((statusData, idx) => {
        const status = this.getMergedStatus(statusData, statusNames);
        const weather = status && status.weather;

        if (
          weather == null ||
          weather.main == null ||
          weather.weather == null ||
          weather.weather.length === 0
        ) {
          return {
            value: [idx, 0],
            draw: false
          };
        }

        const dataPoint = {
          data_arrival_time: statusData.created_at,
          description: weather.weather[0].description,
          temperature: (weather.main.temp - 273.15).toFixed(1),
          minTemperature: (weather.main.temp_min - 273.15).toFixed(1),
          maxTemperature: (weather.main.temp_max - 273.15).toFixed(1),
          icon: weather.weather[0].icon
        };

        const screenBigEnough = window.screen.availWidth > 500;
        const shouldDraw =
          screenBigEnough &&
          (lastRenderedWeatherPoint == null ||
            (new Date(dataPoint.data_arrival_time) -
              new Date(lastRenderedWeatherPoint.data_arrival_time)) /
              3600000 >
              2.5);
        const referencePoint = lastRenderedWeatherPoint || dataPoint;

        if (shouldDraw) {
          lastRenderedWeatherPoint = dataPoint;
        }

        return {
          value: [idx, 0],
          data_arrival_time: dataPoint.data_arrival_time,
          description: dataPoint.description,
          icon: `https://openweathermap.org/img/wn/${dataPoint.icon}@2x.png`,
          minTemperature: Math.min(
            parseFloat(referencePoint.minTemperature),
            parseFloat(dataPoint.temperature)
          ).toFixed(1),
          maxTemperature: Math.max(
            parseFloat(referencePoint.maxTemperature),
            parseFloat(dataPoint.temperature)
          ).toFixed(1),
          draw: shouldDraw
        };
      });

    const renderWeather = function(param, api) {
      const weatherPoint = weatherData[param.dataIndex];

      if (weatherPoint == null || weatherPoint.draw !== true) {
        return null;
      }

      let point = api.coord([api.value(0), 0]);

      return {
        type: "group",
        children: [
          {
            type: "image",
            style: {
              image: weatherPoint.icon,
              x: -weatherIconSize / 2,
              y: -weatherIconSize / 2,
              width: weatherIconSize,
              height: weatherIconSize
            },
            position: [point[0], 40]
          },
          {
            type: "text",
            style: {
              text: `${weatherPoint.minTemperature} - ${weatherPoint.maxTemperature}°C`,
              textFont: api.font({ fontSize: 14 }),
              textAlign: "center",
              textVerticalAlign: "bottom"
            },
            position: [point[0], 82]
          }
        ]
      };
    };

    let options = {
      tooltip: {
        trigger: "axis",
        formatter: function(params) {
          if (params == null || params.length === 0) {
            return "";
          }

          const dataIndex = params[0].dataIndex;
          const dataPoint = dataToday[dataIndex] || {};
          const tooltipLines = [
            dayjs(dataPoint.data_arrival_time)
              .tz(tz)
              .format("LT")
          ];

          params.forEach(seriesPoint => {
            if (seriesPoint.data == null || seriesPoint.seriesName == null) {
              return;
            }

            tooltipLines.push(
              `${seriesPoint.marker}${seriesPoint.seriesName}: ${seriesPoint.data}`
            );
          });

          if (dataPoint.system_status != null && dataPoint.system_status !== "") {
            tooltipLines.push(`System Status: ${dataPoint.system_status}`);
          }

          if (
            dataPoint.net_meter_status != null &&
            dataPoint.net_meter_status !== ""
          ) {
            tooltipLines.push(`Net Meter Status: ${dataPoint.net_meter_status}`);
          }

          if (
            dataPoint.net_meter_power_factor != null &&
            dataPoint.net_meter_power_factor !== ""
          ) {
            tooltipLines.push(
              `Power Factor: ${dataPoint.net_meter_power_factor}`
            );
          }

          return tooltipLines.join("<br/>");
        },
        axisPointer: {
          type: "cross",
          label: {
            backgroundColor: "#6a7985"
          }
        }
      },
      legend: {
        data: visibleChartSeries.map(seriesConfig => seriesConfig.label)
      },
      toolbox: {
        feature: {
          saveAsImage: {}
        }
      },
      grid: {
        left: "1%",
        right: "1%",
        bottom: "1%",
        containLabel: true
      },
      xAxis: [
        {
          boundaryGap: false,
          data: dataToday.map(dp =>
            dayjs(dp.data_arrival_time)
              .tz(tz)
              .format("LT")
          )
        }
      ],
      yAxis: [
        {
          type: "value"
        }
      ],
      series: [
        ...visibleChartSeries.map(seriesConfig => ({
          name: seriesConfig.label,
          type: "line",
          areaStyle: {},
          emphasis: {
            focus: "series"
          },
          data: dataToday.map(dp => dp[seriesConfig.dataKey])
        })),
        {
          type: "custom",
          renderItem: renderWeather,
          data: weatherData,
          tooltip: {
            trigger: "item",
            formatter: function(param) {
              const weatherPoint = param.data;

              if (weatherPoint == null || weatherPoint.description == null) {
                return "";
              }

              return `${dayjs(weatherPoint.data_arrival_time)
                .tz(tz)
                .format("LT")}<br/>${weatherPoint.description}<br/>${
                weatherPoint.minTemperature
              } - ${weatherPoint.maxTemperature}°C`;
            }
          }
        }
      ]
    };

    const chartEvents = {
      click: params => {
        const dataIndex = params && params.dataIndex;

        if (dataIndex == null || dataToday[dataIndex] == null) {
          return;
        }

        const selectedPoint = dataToday[dataIndex];
        const tooltipDetails = {
          timeLabel: dayjs(selectedPoint.data_arrival_time)
            .tz(tz)
            .format("LT"),
          series: visibleChartSeries
            .map(seriesConfig => ({
              label: seriesConfig.label,
              value: selectedPoint[seriesConfig.dataKey]
            }))
            .filter(
              seriesItem =>
                seriesItem.value != null &&
                seriesItem.value !== ""
            ),
          systemStatus: selectedPoint.system_status,
          netMeterStatus: selectedPoint.net_meter_status,
          powerFactor: selectedPoint.net_meter_power_factor
        };

        if (selectedPoint.data_arrival_time) {
          this.handleChartRawDataRequest(
            selectedPoint.data_arrival_time,
            tooltipDetails
          );
        }
      }
    };

    return (
      <div>
        <h3>
          <strong>{`${selectedDay} ${selectedChartLabel} details over time`}</strong>
        </h3>
        {showDateSelection && (
          <div className={classes.chartSelectionContainer}>
            <CustomInput
              labelText="Date"
              formControlProps={{
                fullWidth: true
              }}
              inputProps={{
                type: "date",
                value: selectedChartDate,
                onChange: this.onChartDateChange(chartIdx, "date")
              }}
            />
          </div>
        )}
        <div className={classes.chartSelectionContainer}>
          <label className={classes.chartTypeLabel}>Chart Type</label>
          <CustomSelect
            className={classes.chartTypeSelector}
            labelText={selectedChartLabel}
            items={chartTypeOptions}
            onChange={this.onChartTypeChange(chartIdx, "visibleChartType")}
          />
        </div>
        <ReactEcharts option={options} onEvents={chartEvents} />
      </div>
    );
  }

  renderDeviceLoadInfo(deviceData) {
    const { classes } = this.props;
    const loadData = deviceData.loads_today || {};
    const allStatusData = deviceData.status_data_today || [];
    let currentLoad = [];
    let latestLoadStatus = null;
    if (allStatusData.length > 0) {
      const latestStatusData = allStatusData[allStatusData.length - 1];
      const latestStatus = this.getMergedStatus(
        latestStatusData,
        (deviceData.available_status_types || []).map(st => st.name)
      );

      latestLoadStatus = latestStatus.load_status;
      currentLoad =
        (latestStatusData && latestStatusData.status && latestStatusData.status.loads) || [];
    }
    const currency =
      (deviceData.properties && deviceData.properties.currency) || "$";

    let loadInfo = Object.keys(loadData).map(loadName => {
      let data = { ...loadData[loadName] };
      currentLoad.forEach(loadInfo => {
        if (loadInfo.name.toLowerCase() === loadName.toLowerCase()) {
          data["currentlyRunning"] = loadInfo.qty;
          data["currentPower"] = loadInfo.power;
        }
      });
      data["name"] = loadName;
      return data;
    });

    let totalLoad =
      latestLoadStatus != null && latestLoadStatus !== ""
        ? latestLoadStatus
        : deviceData.latest_data &&
            deviceData.latest_data.load_meter &&
            (deviceData.latest_data.load_meter.power || 0).toFixed(2);

    return (
      loadInfo && (
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <h3>
              <strong
              >{`Today's load details (${totalLoad} W current load)`}</strong>
            </h3>
            {loadInfo &&
              loadInfo.length > 0 && (
                <Table
                  tableHeaderColor="warning"
                  tableHead={[
                    "Load",
                    "Currently Running",
                    "Current Power",
                    "Energy",
                    "Amount",
                    "Hours"
                  ]}
                  tableData={loadInfo.map(load => [
                    <img
                      className={
                        load.currentlyRunning > 0
                          ? classes.loadOnImg
                          : classes.loadOffImg
                      }
                      key={load.name}
                      src={loadIcons[load.name]}
                      alt="..."
                    />,
                    load.currentlyRunning || 0,
                    `${
                      load.currentPower
                        ? (load.currentPower / 1000.0).toFixed(1)
                        : "0"
                    } KW`,
                    `${load.energy.toFixed(1)} KWh`,
                    `${load.money.toFixed(1)} ${currency}`,
                    `${load.hours}:${load.minutes} hrs`
                  ])}
                  coloredColls={[3]}
                  colorsColls={["primary"]}
                />
              )}
          </GridItem>
        </GridContainer>
      )
    );
  }

  renderDevice(deviceData) {
    // const { classes } = this.props;
    if (
      deviceData.other_data.enableStatusCharts !== true
    ) {
      return <h6>Status not available!</h6>;
    }

    return (
      <div>
        {this.renderStatusSummary(deviceData)}
        {this.renderDeviceTips(deviceData)}
        {this.renderDeviceLoadInfo(deviceData)}
        {this.renderConsumptionChart(deviceData, true)}
        <AdvancedCharts deviceData={deviceData} axios={this.props.axios} />
      </div>
    );
  }

  renderAlarmDetails(deviceData) {
    const { classes } = this.props;
    const statusKeys = this.getStatusKeysFromDeviceData(deviceData).map(statusKey => ({
      text: statusKey,
      value: statusKey
    }));
    const alarmData = this.state.alarmData || {};
    const alarmRules = this.state.alarmRules || [];
    const alarmHistory = this.state.alarmHistory || [];

    return (
      <div className={classes.alarmsContainer}>
        <div className={classes.alarmActions}>
          <Button
            size="sm"
            color="info"
            className={classes.marginRight}
            onClick={() => this.openAlarmModal(deviceData)}
          >
            Add new
          </Button>
          <Button
            size="sm"
            color="primary"
            className={classes.marginRight}
            onClick={() => this.refreshAlarmData(this.state.deviceId)}
          >
            Refresh
          </Button>
          <Modal
            hideBackdrop
            open={this.state.alarmModalOpen}
            onClose={() => this.setState({ alarmModalOpen: false })}
            aria-labelledby="Add status alarm"
            aria-describedby="Create status rule alarm"
          >
            <Box className={classes.alarmModalBox} sx={{ width: 480 }}>
              <div className={classes.alarmSelectionContainer}>
                <CustomInput
                  labelText="Rule name"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    value: alarmData.name,
                    onChange: this.onChange("alarmData", "name")
                  }}
                />
              </div>
              <div className={classes.alarmSelectionContainer}>
                <label className={classes.chartTypeLabel}>Status field</label>
                <CustomSelect
                  className={classes.alarmTypeSelector}
                  labelText={alarmData.status_key || "Select status field"}
                  items={statusKeys}
                  onChange={this.onSelectionChange("alarmData", "status_key")}
                />
              </div>
              <div className={classes.alarmSelectionContainer}>
                <label className={classes.chartTypeLabel}>Operator</label>
                <CustomSelect
                  className={classes.alarmTypeSelector}
                  labelText={alarmData.operator || ALARM_OPERATORS[0].value}
                  items={ALARM_OPERATORS}
                  onChange={this.onSelectionChange("alarmData", "operator")}
                />
              </div>
              <div className={classes.alarmSelectionContainer}>
                <CustomInput
                  labelText="Target value"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    value: alarmData.target_value,
                    onChange: this.onChange("alarmData", "target_value")
                  }}
                />
              </div>
              <div className={classes.alarmSelectionContainer}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!alarmData.notifyInApp}
                      onChange={() =>
                        this.setState({
                          alarmData: {
                            ...alarmData,
                            notifyInApp: !alarmData.notifyInApp
                          }
                        })
                      }
                    />
                  }
                  label="In-app"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!alarmData.notifyTelegram}
                      onChange={() =>
                        this.setState({
                          alarmData: {
                            ...alarmData,
                            notifyTelegram: !alarmData.notifyTelegram
                          }
                        })
                      }
                    />
                  }
                  label="Telegram"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!alarmData.notifyEmail}
                      onChange={() =>
                        this.setState({
                          alarmData: {
                            ...alarmData,
                            notifyEmail: !alarmData.notifyEmail
                          }
                        })
                      }
                    />
                  }
                  label="Email"
                />
              </div>
              {alarmData.notifyEmail && (
                <div className={classes.alarmSelectionContainer}>
                  <CustomInput
                    labelText="Emails (comma separated)"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      value: alarmData.emails,
                      onChange: this.onChange("alarmData", "emails")
                    }}
                  />
                </div>
              )}
              {alarmData.notifyTelegram && (
                <div className={classes.alarmSelectionContainer}>
                  <CustomInput
                    labelText="Telegram chat IDs (comma separated)"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      value: alarmData.telegram_chat_ids,
                      onChange: this.onChange("alarmData", "telegram_chat_ids")
                    }}
                  />
                </div>
              )}
              <div className={classes.alarmSelectionContainer}>
                <Button onClick={() => this.saveAlarmRule()}>
                  Save
                </Button>
                <Button onClick={() => this.setState({ alarmModalOpen: false })}>
                  Cancel
                </Button>
              </div>
            </Box>
          </Modal>
        </div>

        {this.state.alarmsError && (
          <Typography color="error">{this.state.alarmsError}</Typography>
        )}
        {this.state.alarmsLoading && (
          <Typography>Syncing alarms...</Typography>
        )}

        <h5>
          <strong>Alarm Rules</strong>
        </h5>
        {alarmRules.length > 0 ? (
          <Table
            tableHeaderColor="primary"
            tableHead={["Name", "Condition", "Notifications", "Last Trigger", "Action"]}
            tableData={alarmRules.map(alarm => [
              alarm.name || "Unnamed",
              `${alarm.status_key} ${alarm.operator} ${alarm.target_value}`,
              (alarm.channels || []).join(", "),
              alarm.last_trigger_time
                ? dayjs(alarm.last_trigger_time).format(DATE_DISPLAY_FORMAT_DETAILED)
                : "Never",
              <Button
                key={`delete-alarm-${alarm.id}`}
                size="sm"
                color="danger"
                onClick={() => this.removeAlarmRule(alarm.id)}
              >
                Delete
              </Button>
            ])}
            coloredColls={[3]}
            colorsColls={["primary"]}
          />
        ) : (
          <h6>No alarm rules configured.</h6>
        )}

        <h5>
          <strong>Triggered Alarms History</strong>
        </h5>
        {alarmHistory.length > 0 ? (
          <Table
            tableHeaderColor="warning"
            tableHead={[
              "Triggered at",
              "Rule",
              "Condition",
              "Matched value",
              "Channels",
              "Message"
            ]}
            tableData={alarmHistory.map(history => [
              dayjs(history.triggered_at).format(DATE_DISPLAY_FORMAT_DETAILED),
              history.alarm_name || "Unnamed",
              `${history.status_key} ${history.operator} ${history.target_value}`,
              history.status_value || "",
              (history.channels || []).join(", "),
              history.message || ""
            ])}
            coloredColls={[3]}
            colorsColls={["primary"]}
          />
        ) : (
          <h6>No alarms triggered yet.</h6>
        )}
      </div>
    );
  }

  renderDeviceCommands(deviceData) {
    return (
      <DeviceCommands
        axios={this.props.axios}
        user={this.props.user}
        deviceData={deviceData}
      />
    );
  }

  renderLatestTelemetry(deviceData) {
    const { classes, user } = this.props;
    const userPermissions = user.user_permissions || [];
    const userCanSeeRawDataDetails = userPermissions.includes(
      CAN_SEE_RAW_DATA_DETAILS
    );
    const statusTypes = deviceData.available_status_types || [];
    const availableStatusTypes =
      statusTypes == null || statusTypes.length === 0
        ? [
            { value: "raw_data", text: "Raw Data" },
            { value: "DAILY_STATUS", text: "Daily Status" }
          ]
        : [
            ...statusTypes.map(stTp => {
              return { value: stTp.target_type, text: stTp.name };
            }),
            { value: "raw_data", text: "Raw Data" }
          ];
    const latestTelemetryView = this.state.latestTelemetryView || "raw";
    const downloadData = this.state.downloadData || {};
    return (
      <div className={classes.alarmsContainer}>
        <div className={classes.telemetrySubTabsBar}>
          <div className={classes.telemetrySubTabs}>
            <Tabs
              value={latestTelemetryView}
              onChange={(event, value) =>
                this.setState({ latestTelemetryView: value, statusDataPage: 0 })
              }
              className={classes.telemetryTabsRoot}
              classes={{ indicator: classes.telemetryTabsIndicator }}
            >
              <Tab
                label="Raw Data"
                value="raw"
                className={`${classes.telemetrySubTabButton} ${classes.telemetrySubTabButtonInactive}`}
                classes={{ selected: classes.telemetrySubTabButtonSelected }}
              />
              <Tab
                label="Status Data"
                value="status"
                className={`${classes.telemetrySubTabButton} ${classes.telemetrySubTabButtonInactive}`}
                classes={{ selected: classes.telemetrySubTabButtonSelected }}
              />
            </Tabs>
          </div>
          <Button
            color="info"
            size="sm"
            round
            className={classes.telemetryDownloadButton}
            onClick={() => this.setState({ dataDownloadModalOpen: true })}
          >
            Download
          </Button>
          <Modal
            hideBackdrop
            open={this.state.dataDownloadModalOpen}
            onClose={() => this.setState({ dataDownloadModalOpen: false })}
            aria-labelledby="Download"
            aria-describedby="Download Data"
          >
            <Box className={classes.alarmModalBox} sx={{ width: 200 }}>
              <div className={classes.alarmSelectionContainer}>
                <label className={classes.chartTypeLabel}>Data Type</label>
                <CustomSelect
                  className={classes.alarmTypeSelector}
                  labelText={downloadData.dataType || "raw_data"}
                  items={availableStatusTypes}
                  onChange={this.onSelectionChange("downloadData", "dataType")}
                />
              </div>
              <div className={classes.alarmSelectionContainer}>
                <CustomInput
                  labelText="Start Date"
                  formControlProps={{
                    fullWidth: true
                  }}
                  inputProps={{
                    type: "date",
                    value: downloadData.startDate,
                    onChange: this.onChange("downloadData", "startDate")
                  }}
                />
              </div>
              <div className={classes.alarmSelectionContainer}>
                <CustomInput
                  labelText="End Date"
                  formControlProps={{
                    fullWidth: true
                  }}
                  inputProps={{
                    type: "date",
                    value: downloadData.endDate,
                    onChange: this.onChange("downloadData", "endDate")
                  }}
                />
              </div>
              <div className={classes.alarmSelectionContainer}>
                <Button onClick={() => this.downloadDeviceData()}>Download</Button>
                <Button
                  onClick={() =>
                    this.setState({ dataDownloadModalOpen: false })
                  }
                >
                  Cancel
                </Button>
              </div>
            </Box>
          </Modal>
        </div>

        <div>
          {latestTelemetryView === "status"
            ? this.renderStatusData(deviceData)
            : this.renderRawData(deviceData, userCanSeeRawDataDetails)}
        </div>
      </div>
    );
  }

  renderRawData(deviceData, userCanSeeRawDataDetails) {
    const { classes } = this.props;
    const latestData = deviceData.latest_data;
    const keysToHide = userCanSeeRawDataDetails ? [] : RAW_DATA_KEYS_TO_HIDE;
    const latestRawSnapshots = latestData.raw_data_last_5 || [];

    return (
      <div className={classes.rawTelemetryDataContainer}>
        {latestData.raw_data_last_5 ? (
          latestRawSnapshots.length > 0 ? (
            latestRawSnapshots.map((data, idx) => {
                return (
                  <ExpansionPanel
                    key={`raw-data-${idx}`}
                    defaultExpanded={idx === 0}
                  >
                    <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography className={classes.heading}>
                        Updated:{" "}
                        {dayjs.utc(data.data_arrival_time).local().fromNow()}
                      </Typography>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails>
                      {prettyJson(data, classes, keysToHide)}
                    </ExpansionPanelDetails>
                  </ExpansionPanel>
                );
              })
          ) : (
            <Typography>No raw telemetry snapshots available.</Typography>
          )
        ) : (
          prettyJson(latestData, classes, keysToHide)
        )}
      </div>
    );
  }

  renderStatusData(deviceData) {
    const { classes } = this.props;
    const allStatusData = [...(deviceData.status_data_today || [])].reverse();
    const keysToHide = [];
    const pageSize = 10;
    const totalPages = Math.max(1, Math.ceil(allStatusData.length / pageSize));
    const currentPage = Math.min(this.state.statusDataPage || 0, totalPages - 1);
    const start = currentPage * pageSize;
    const end = start + pageSize;
    const paginatedStatusData = allStatusData.slice(start, end);

    if (allStatusData.length === 0) {
      return (
        <div className={classes.rawTelemetryDataContainer}>
          <Typography>No status_data_today records available.</Typography>
        </div>
      );
    }

    return (
      <div className={classes.rawTelemetryDataContainer}>
        <div className={classes.statusPaginationBar}>
          <Typography className={classes.statusPaginationInfo}>
            Page {currentPage + 1} of {totalPages} ({allStatusData.length} records)
          </Typography>
          <div className={classes.statusPaginationButtons}>
            <Button
              color="white"
              size="sm"
              round
              disabled={currentPage <= 0}
              onClick={() =>
                this.setState({ statusDataPage: Math.max(currentPage - 1, 0) })
              }
            >
              Previous
            </Button>
            <Button
              color="info"
              size="sm"
              round
              disabled={currentPage >= totalPages - 1}
              onClick={() =>
                this.setState({
                  statusDataPage: Math.min(currentPage + 1, totalPages - 1)
                })
              }
            >
              Next
            </Button>
          </div>
        </div>
        {paginatedStatusData.map((data, idx) => {
          const statusSnapshot =
            data && typeof data.status === "object" && data.status != null
              ? data.status
              : data;
          const updatedAt = data && (data.created_at || data.data_arrival_time);
          return (
            <ExpansionPanel
              key={`status-data-${start + idx}`}
              defaultExpanded={idx === 0}
            >
              <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                <Typography className={classes.heading}>
                  Updated: {updatedAt ? dayjs.utc(updatedAt).local().fromNow() : "Unknown"}
                </Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                {prettyJson(statusSnapshot, classes, keysToHide)}
              </ExpansionPanelDetails>
            </ExpansionPanel>
          );
        })}
      </div>
    );
  }

  render() {
    const { classes, user } = this.props;
    const userPermissions = (user && user.user_permissions) || [];
    const userCanSeeLogs =
      userPermissions.includes(PERMISSIONS_ADMIN) ||
      userPermissions.includes(CAN_SEE_DEVICE_LOGS);
    const { deviceData } = this.state;
    if (deviceData === undefined) return null;

    const latestData = deviceData.latest_data;
    const properties = deviceData.properties;
    const latestStatus = this.getLatestMergedStatus(deviceData);
    const weatherData = deviceData.latest_weather_data;
    let netMeterStatus = latestStatus.net_meter_status;
    let batteryStatus = latestStatus.battery_charging_status;
    let loadStatus = latestStatus.load_status;
    let solarStatus = latestStatus.solar_status;
    let systemTemperature = latestStatus.system_temperature;

    if (netMeterStatus == null) {
      netMeterStatus = properties && properties.net_meter_status;
    }
    if (batteryStatus == null) {
      batteryStatus = properties && properties.battery_charging_status;
    }
    if (loadStatus == null) {
      loadStatus = properties && properties.load_status;
    }
    if (solarStatus == null) {
      solarStatus = properties && properties.solar_status;
    }
    if (systemTemperature == null) {
      systemTemperature = properties && properties.system_temperature;
    }

    const formatLcdValue = value => {
      if (value == null || value === "") {
        return value;
      }

      if (typeof value === "number" && Number.isFinite(value)) {
        return value.toFixed(1);
      }

      if (typeof value === "string") {
        const trimmedValue = value.trim();
        if (trimmedValue !== "" && !Number.isNaN(Number(trimmedValue))) {
          return Number(trimmedValue).toFixed(1);
        }
      }

      return value;
    };

    const formattedNetMeterStatus = formatLcdValue(netMeterStatus);
    const formattedSolarStatus = formatLcdValue(solarStatus);
    const formattedBatteryStatus = formatLcdValue(batteryStatus);
    const formattedLoadStatus = formatLcdValue(loadStatus);
    const formattedSystemTemperature = formatLcdValue(systemTemperature);

    const hasLcdStatusData = [
      netMeterStatus,
      solarStatus,
      batteryStatus,
      loadStatus,
      systemTemperature
    ].some(value => value != null && value !== "");

    let lastUpdateTime = latestData.last_update_time;
    let lastHeartbeatTime =
      deviceData.other_data && deviceData.other_data.last_heartbeat_time;

    if (lastUpdateTime == null) {
      lastUpdateTime =
        deviceData.other_data && deviceData.other_data.last_data_sync_time;
    }

    let deviceAvatar = `${MEDIA_ROOT}${deviceData.avatar}`;

    let availableTabs = [];
    if (
      deviceData.other_data.enableStatusCharts === true
    ) {
      availableTabs.push({
        tabButton: "Status",
        tabIcon: SettingsApplicationsIcon,
        tabContent: this.renderDevice(deviceData)
      });
    }
    availableTabs = availableTabs.concat([
      {
        tabButton: "Latest telemetry",
        tabIcon: TimelineIcon,
        tabContent: this.renderLatestTelemetry(deviceData)
      },
      {
        tabButton: "Alarms",
        tabIcon: AlarmIcon,
        tabContent: this.renderAlarmDetails(deviceData)
      },
      {
        tabButton: "Details",
        tabIcon: InfoIcon,
        tabContent: (
          <DeviceDetailsTable
            deviceData={deviceData}
            user={this.props.user}
            axios={this.props.axios}
          />
        )
      }
    ]);

    if (userCanSeeLogs) {
      availableTabs.push({
        tabButton: "Device Logs",
        tabIcon: ListIcon,
        tabContent: (
          <DeviceLogs
            axios={this.props.axios}
            user={this.props.user}
            deviceData={deviceData}
          />
        )
      });
      availableTabs.push({
        tabButton: "Commands",
        tabIcon: CodeIcon,
        tabContent: this.renderDeviceCommands(deviceData)
      });
    }

    return (
      <GridContainer key={`project-detail`} className={classes.pageShell}>
        <GridItem
          xs={12}
          sm={12}
          md={12}
          key={`project-${this.props.projectId}`}
        >
          <Card chart className={classes.cardHover}>
            <CardHeader className={classes.cardHeaderHover}>
              <CardAvatar project className={classes.heroAvatarFrame}>
                {hasLcdStatusData ? (
                  <LCD
                    className={classes.lcd}
                    backlit
                    lines={[
                      netMeterStatus != null && `Status: ${formattedNetMeterStatus} W`,
                      solarStatus != null && `Solar: ${formattedSolarStatus} W`,
                      batteryStatus != null && `Battery: ${formattedBatteryStatus} W`,
                      (loadStatus != null || systemTemperature != null) &&
                        `Load: ${loadStatus != null ? formattedLoadStatus : "NA"} W, ${
                          systemTemperature != null ? formattedSystemTemperature : "NA"
                        }°C`,
                      lastUpdateTime &&
                        `Updated: ${dayjs(lastUpdateTime).fromNow()}`
                    ]}
                  />
                ) : (
                  deviceData.avatar && (
                    <a href="#avatar" onClick={e => e.preventDefault()}>
                      <img
                        className={classes.img}
                        src={deviceAvatar}
                        alt="..."
                      />
                    </a>
                  )
                )}
              </CardAvatar>
              <div className={classes.deviceInfoHeaderContainer}>
                <div className={classes.deviceInfo}>
                  <div className={classes.deviceTitleRow}>
                    <h2 className={classes.cardTitleBlack}>
                      {deviceData.name || deviceData.alias}
                    </h2>
                    <span className={classes.deviceMetaPill}>
                      {deviceData.ip_address}
                    </span>
                  </div>
                  {lastHeartbeatTime != null && (
                    <div className={classes.timestampPanel}>
                      <div className={classes.updateInfo}>
                        <label className={classes.updateInfoLabel}>
                          Last update:
                        </label>
                        <span>
                          {dayjs(lastHeartbeatTime).format(
                            DATE_DISPLAY_FORMAT_DETAILED
                          )}
                          , {dayjs(lastHeartbeatTime).fromNow()}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className={classes.timestampPanel}>
                    <div className={classes.dataUpdateInfo}>
                      <label className={classes.dataUpdateInfoLabel}>
                        Last sync:
                      </label>
                      {lastUpdateTime != null ? (
                        <span>
                          {dayjs(lastUpdateTime).format(
                            DATE_DISPLAY_FORMAT_DETAILED
                          )}
                          , {dayjs(lastUpdateTime).fromNow()}
                        </span>
                      ) : null}
                    </div>
                    <Button
                      size="sm"
                      color="info"
                      round
                      justIcon
                      onClick={() => {
                        const deviceId = this.state.deviceId;
                        if (deviceId) {
                          getUserDevices(this.props.axios, deviceId).then(
                            data => {
                              this.setState({ deviceData: data });
                              this.refreshAlarmData(deviceId);
                            }
                          );
                        }
                      }}
                      className={classes.refreshButton}
                    >
                      <RefreshIcon />
                    </Button>
                  </div>
                </div>
                {weatherData &&
                  weatherData.main &&
                  weatherData.weather &&
                  weatherData.sys && (
                    <div className={classes.deviceWeatherInfo}>
                      <div className={classes.cityName}>
                        <h2 className={classes.cardTitleBlack}>
                          <span>{weatherData.name}</span>
                          <sup>{weatherData.sys.country}</sup>
                        </h2>
                        <div className={classes.cityTemperature}>
                          {Math.round(weatherData.main.temp - 273.15, 1)}
                          <sup>&deg;C</sup>
                        </div>
                      </div>
                      <div className={classes.cityWeatherImage}>
                        <img
                          className={classes.cityWeatherIcon}
                          src={`https://openweathermap.org/img/wn/${
                            weatherData.weather[0].icon
                          }@2x.png`}
                          alt={weatherData.weather[0].description}
                        />
                        <strong>{weatherData.weather[0].description}</strong>
                      </div>
                    </div>
                  )}
              </div>
            </CardHeader>
            <CardBody className={classes.cardBody}>
              <NavPills
                color="primary"
                vertical={{
                  tabsGrid: { xs: 12, sm: 12, md: 12 },
                  contentGrid: { xs: 12, sm: 12, md: 12 }
                }}
                tabs={availableTabs}
                onChange={this.handleTabChange}
              />
            </CardBody>
            <CardFooter stats>
              <div className={classes.footerLinks}>
                <div className={classes.footerLinkGroup}>
                  <a href="/dashboard" className={classes.footerLink}>
                    Home
                  </a>
                  <a
                    href={`/devicereports/${deviceData.ip_address}/`}
                    className={classes.footerLink}
                  >
                    Report
                  </a>
                </div>
                <Typography variant="caption" color="textSecondary">
                  Device overview and telemetry workspace
                </Typography>
              </div>
            </CardFooter>
          </Card>
          {this.renderChartRawDataModal()}
        </GridItem>
      </GridContainer>
    );
  }
}

DeviceDetails.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object,
  location: PropTypes.object
};

export default withStyles(styles)(DeviceDetails);
